import { Link } from "react-router-dom";
import { Leaf, Users2, Scale, Check, ArrowRight, Calculator, FileText, FolderDown } from "lucide-react";
import { GREEN, BLUE, SectionTitle, FlowArrow, EvaMark } from "./shared";

export function EsgSection() {
  return (
    <section className="py-20 lg:py-24" data-testid="section-esg">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="lp-reveal">
          <SectionTitle
            kicker="Tre pilastri, un solo motore"
            title="Environmental, Social, Governance."
            sub="Ogni pilastro ha un motore dedicato che calcola i KPI e alimenta automaticamente VSME e dossier banca." />
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { letter: "E", name: "Environmental", icon: Leaf, c: GREEN, bg: "#F2F9F5",
              items: ["Energia", "Scope 1", "Scope 2", "Acqua", "Rifiuti", "Biodiversità", "Clima"] },
            { letter: "S", name: "Social", icon: Users2, c: "#2F5D8A", bg: "#EFF4FA",
              items: ["Workforce", "Turnover", "Formazione", "Sicurezza", "Gender Pay Gap", "Contrattazione"] },
            { letter: "G", name: "Governance", icon: Scale, c: "#5B7CA3", bg: "#F1F4F8",
              items: ["Policy", "Codice etico", "231", "Anticorruzione", "Whistleblowing", "Organo di governance"] },
          ].map(({ letter, name, icon: Icon, c, bg, items }) => (
            <div key={letter} className="lp-reveal rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              data-testid={`pillar-${letter.toLowerCase()}`}>
              <div className="p-6" style={{ background: bg }}>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-lg flex items-center justify-center text-lg font-extrabold text-white" style={{ background: c, fontFamily: "Manrope, sans-serif" }}>{letter}</span>
                  <div>
                    <h3 className="text-lg font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>{name}</h3>
                  </div>
                  <Icon className="w-5 h-5 ml-auto" style={{ color: c }} aria-hidden="true" />
                </div>
              </div>
              <div className="p-6 bg-white">
                <ul className="grid grid-cols-2 gap-x-3 gap-y-2">
                  {items.map((it) => (
                    <li key={it} className="flex items-center gap-1.5 text-sm text-slate-700">
                      <Check className="w-3.5 h-3.5 shrink-0" style={{ color: c }} aria-hidden="true" />{it}
                    </li>
                  ))}
                </ul>
                <Link to={`/${name.toLowerCase()}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold transition-colors"
                  style={{ color: c }} data-testid={`cta-pillar-${letter.toLowerCase()}`}>
                  Approfondisci {name} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CarbonSection() {
  return (
    <section className="py-20 lg:py-24" style={{ background: "#FAFBFA" }} data-testid="section-carbon">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="lp-reveal">
          <SectionTitle
            kicker="Carbon Calculator"
            title="Dai consumi alle emissioni."
            sub="Ogni calcolo mantiene fonte, fattore, metodologia e anno. I fattori di emissione sono ufficiali e versionati: niente numeri senza provenienza." />
        </div>
        <div className="lp-reveal rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="carbon-example">
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Consumo</p>
              <p className="text-lg font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>10.000 kWh</p>
            </div>
            <ArrowRight className="w-5 h-5" style={{ color: "#F28C28" }} aria-hidden="true" />
            <div className="rounded-lg px-4 py-3" style={{ background: "#E9EFF6" }}>
              <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: BLUE }}>Fattore di emissione</p>
              <p className="text-sm font-bold" style={{ color: BLUE }}>fonte ufficiale · versionato per anno</p>
            </div>
            <ArrowRight className="w-5 h-5" style={{ color: "#F28C28" }} aria-hidden="true" />
            <div className="rounded-lg px-4 py-3" style={{ background: "#E7F4EE" }}>
              <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: GREEN }}>Scope 2</p>
              <p className="text-lg font-extrabold" style={{ color: GREEN, fontFamily: "Manrope, sans-serif" }}>tCO₂e calcolate</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <Calculator className="w-3.5 h-3.5" aria-hidden="true" />
            Fonte, fattore, formula e anno sempre visibili nel drill-down.
          </div>
          <p className="mt-2 text-[11px] text-slate-400 italic">
            Esempio dimostrativo. Il fattore effettivo dipende da anno, fonte, metodologia e perimetro di calcolo.
          </p>
          <Link to="/carbon-calculator"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-md border-2 transition-colors"
            style={{ borderColor: GREEN, color: GREEN }} data-testid="cta-carbon">
            Scopri il Carbon Calculator <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function EvaSection() {
  return (
    <section id="eva" className="py-20 lg:py-24 scroll-mt-20" data-testid="section-eva">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="lp-reveal order-2 lg:order-1 rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-900/5 overflow-hidden" data-testid="eva-chat-preview">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2.5">
            <EvaMark size={28} />
            <div>
              <p className="text-sm font-bold text-slate-900">Eva</p>
              <p className="text-[10px] text-slate-500">ESG Assistant · integrata in ESG-PMI</p>
            </div>
          </div>
          <div className="p-5 space-y-4" aria-label="Esempio dimostrativo di conversazione con Eva — dati simulati a fini illustrativi">
            <div className="flex justify-end">
              <p className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm text-white" style={{ background: GREEN }}>
                Eva, cosa manca per completare il VSME?
              </p>
            </div>
            <div className="flex gap-2.5">
              <EvaMark size={28} />
              <p className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm bg-slate-50 border border-slate-200 text-slate-800">
                Il VSME Basic è completo all'87%. Mancano 4 informazioni: due relative all'acqua,
                una ai rifiuti e una alla formazione.
              </p>
            </div>
            <div className="flex justify-end">
              <p className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm text-white" style={{ background: GREEN }}>
                Da dove arrivano i dati sulla formazione?
              </p>
            </div>
            <div className="flex gap-2.5">
              <EvaMark size={28} />
              <div className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm bg-slate-50 border border-slate-200 text-slate-800">
                Da JOBTIMEHR, ultima sincronizzazione 5 settembre 2026.
                <span className="block mt-1.5 text-[10px] font-semibold" style={{ color: BLUE }}>
                  Fonte: JOBTIMEHR Sync · dato importato
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="lp-reveal order-1 lg:order-2">
          <div className="flex items-center gap-3">
            <EvaMark size={44} />
            <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
              Chiedilo a Eva.
            </h2>
          </div>
          <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
            Eva è l'assistente intelligente integrato in ESG-PMI. Risponde usando solo i dati
            autorizzati della tua azienda: ti dice cosa manca, spiega i KPI e dichiara sempre
            la fonte di ogni numero.
          </p>
          <ul className="mt-6 space-y-2.5">
            {["Conosce azienda, periodo e framework attivi",
              "Non inventa dati: se un'informazione manca, lo dice",
              "Rispetta permessi e ruoli di ogni utente"].map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GREEN }} aria-hidden="true" />{t}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[11px] text-slate-400 italic">Esempio di conversazione — dati dimostrativi.</p>
          <Link to="/eva"
            className="mt-4 inline-flex items-center gap-2 text-sm font-bold px-6 py-3 rounded-md text-white transition-transform hover:-translate-y-0.5"
            style={{ background: BLUE }} data-testid="cta-eva">
            Scopri Eva <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function DocumentiSection() {
  return (
    <section className="py-20 lg:py-24" style={{ background: "#FAFBFA" }} data-testid="section-documenti">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="lp-reveal">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
              Carica i documenti. ESG-PMI trova i dati.
            </h2>
            <span className="shrink-0 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full"
              style={{ background: "#FBF0E1", color: "#B85E0F" }} data-testid="documenti-badge">
              In arrivo
            </span>
          </div>
          <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
            Bollette, MUD, DVR, policy e attestati di formazione: il Cassetto ESG con Document AI
            estrarrà i dati dai documenti e li proporrà per la verifica. Tu confermi, ESG-PMI compila.
          </p>
          <Link to="/cassetto-esg"
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold px-6 py-3 rounded-md border-2 transition-colors"
            style={{ borderColor: GREEN, color: GREEN }} data-testid="cta-cassetto">
            Scopri il Cassetto ESG
          </Link>
        </div>
        <div className="lp-reveal rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap gap-2 mb-4">
            {["Bollette", "MUD", "DVR", "Policy", "Formazione"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 text-slate-700">
                <FileText className="w-3 h-3" style={{ color: BLUE }} aria-hidden="true" />{t}
              </span>
            ))}
          </div>
          <FlowArrow />
          <div className="rounded-lg border-2 p-4 text-center" style={{ borderColor: BLUE, background: "#EFF4FA" }}>
            <FolderDown className="w-5 h-5 mx-auto" style={{ color: BLUE }} aria-hidden="true" />
            <p className="mt-1.5 text-sm font-bold" style={{ color: BLUE }}>Cassetto ESG</p>
          </div>
          <FlowArrow />
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-center">
            <p className="text-sm font-semibold text-slate-800">Dati proposti, da verificare con un click</p>
          </div>
        </div>
      </div>
    </section>
  );
}
